/**
 * The in-thread question card's state (agion/Agion#3446, challenge
 * 2026-10-26-escalation-question-chat-card C3 and C5). The card reads its
 * escalation from governance under the viewer's own session; the frame that
 * placed it holds only the id. What the card offers follows governance alone:
 *
 * - can_decide true on a pending single-choice or free-text question: the
 *   question and its answer, sent through the escalation store as the
 *   Escalations sections send it;
 * - can_decide false or absent, or a read that failed: the waiting line and
 *   the link to Escalations (fail-closed);
 * - a 403 or 404, or a frame naming no escalation: the waiting line only;
 * - any other status than pending: closed.
 *
 * The card reads again when an answer from this browser settles or is
 * refused (a 409 re-reads in the store, and so here), when the store learns
 * that the escalation is no longer pending, and every 30 s while it is open,
 * so an answer given elsewhere closes it. A read that failed (not a refusal)
 * is tried again after 5 s, 15 s and 30 s, then every 30 s. Nothing is read
 * while the tab is hidden; a read follows when it is visible again. The
 * latest read wins.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChoiceOrTextQuestion, Escalation } from '../types/escalations'
import { isChatApiError } from '../services/api-failure'
import { isChoiceOrTextQuestion, recordedAnswerText } from '../services/escalation-answer'
import { escalationApi, useEscalationStore } from './useEscalations'

/** How often an open card reads its escalation again, as the Escalations sections poll. */
export const QUESTION_CARD_POLL_MS = 30_000

/** The waits before the first retries of a failed read; later ones wait QUESTION_CARD_POLL_MS. */
export const QUESTION_CARD_RETRY_MS: readonly number[] = [5_000, 15_000, 30_000]

export type QuestionCardView =
  | { kind: 'loading' }
  /** Governance refused the read (403 or 404), or the frame named no escalation: no link. */
  | { kind: 'unreadable' }
  /** The viewer may not decide here: the line and the link. */
  | { kind: 'waiting' }
  | { kind: 'decide'; escalation: Escalation; question: ChoiceOrTextQuestion }
  /**
   * No longer pending. answered: governance recorded an answer, by
   * answeredByName when it names them. answerText: the chosen option's label
   * of a single-choice question, as the Escalations sections show it.
   * keptWithRun: the answer stays with the run (free text and forms); the
   * card never shows such an answer's text.
   */
  | { kind: 'closed'; answered: boolean; answeredByName?: string; answerText?: string; keptWithRun: boolean }

type Read =
  | { kind: 'loading' }
  | { kind: 'refused' }
  /** No read has succeeded yet; failures counts the reads that failed. */
  | { kind: 'failed'; failures: number }
  | { kind: 'read'; escalation: Escalation }

function isEscalation(value: unknown): value is Escalation {
  return Boolean(value) && typeof value === 'object' && typeof (value as Escalation).id === 'string'
}

/** What the card shows for a read: governance's answer, never the browser's own. */
function questionCardView(read: Read): QuestionCardView {
  switch (read.kind) {
    case 'loading':
      return { kind: 'loading' }
    case 'refused':
      return { kind: 'unreadable' }
    case 'failed':
      return { kind: 'waiting' }
  }
  const { escalation } = read
  if (escalation.status !== 'pending') {
    const answered = escalation.status === 'answered'
    const question = escalation.question
    const type = question?.type
    // Only a single choice's option label; recordedAnswerText would give a
    // free text's text, which the card never shows.
    const answerText = answered && question && type === 'single_choice' ? recordedAnswerText(question, escalation.answer) : undefined
    return {
      kind: 'closed',
      answered,
      ...(answered && escalation.answered_by_name ? { answeredByName: escalation.answered_by_name } : {}),
      ...(answerText ? { answerText } : {}),
      keptWithRun: answered && (type === 'free_text' || type === 'form'),
    }
  }
  if (escalation.can_decide === true && isChoiceOrTextQuestion(escalation.question)) {
    return { kind: 'decide', escalation, question: escalation.question }
  }
  return { kind: 'waiting' }
}

function failedAgain(prev: Read): Read {
  return { kind: 'failed', failures: prev.kind === 'failed' ? prev.failures + 1 : 1 }
}

/** The wait before the next read, or undefined when the card reads no more on its own. */
function nextReadDelay(read: Read): number | undefined {
  if (read.kind === 'failed') return QUESTION_CARD_RETRY_MS[read.failures - 1] ?? QUESTION_CARD_POLL_MS
  if (read.kind === 'read' && read.escalation.status === 'pending') return QUESTION_CARD_POLL_MS
  return undefined
}

function useTabVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden')
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])
  return visible
}

export function useQuestionCard(escalationId: string | undefined): QuestionCardView {
  const [read, setRead] = useState<Read>(escalationId ? { kind: 'loading' } : { kind: 'refused' })
  // Bumped after every read, so the next one is scheduled even when the state stays the same.
  const [readCount, setReadCount] = useState(0)
  const latest = useRef(0)

  const reread = useCallback(async () => {
    if (!escalationId) return
    const attempt = ++latest.current
    let next: (prev: Read) => Read
    try {
      const fresh = await escalationApi.get(escalationId)
      next = isEscalation(fresh)
        ? () => ({ kind: 'read', escalation: fresh })
        : (prev) => (prev.kind === 'read' ? prev : failedAgain(prev))
    } catch (error) {
      const refused = isChatApiError(error) && (error.status === 403 || error.status === 404)
      // A failed re-read keeps the last state governance gave.
      next = (prev) => (refused ? { kind: 'refused' } : prev.kind === 'read' ? prev : failedAgain(prev))
    }
    if (attempt !== latest.current) return
    setRead(next)
    setReadCount((n) => n + 1)
  }, [escalationId])

  useEffect(() => {
    setRead(escalationId ? { kind: 'loading' } : { kind: 'refused' })
    void reread()
    // A read still on its way after unmount or a new id is dropped.
    return () => {
      latest.current += 1
    }
  }, [escalationId, reread])

  const open = read.kind === 'read' && read.escalation.status === 'pending'
  const delay = nextReadDelay(read)
  const visible = useTabVisible()

  // The next read on its own: after the retry wait or the poll interval, and
  // only while the tab is visible.
  useEffect(() => {
    if (!visible || delay === undefined) return undefined
    const timer = setTimeout(() => void reread(), delay)
    return () => clearTimeout(timer)
  }, [visible, delay, readCount, reread])

  // Visible again: read at once instead of waiting out the interval.
  const wasVisible = useRef(visible)
  useEffect(() => {
    const becameVisible = visible && !wasVisible.current
    wasVisible.current = visible
    if (becameVisible && delay !== undefined) void reread()
  }, [visible, delay, reread])

  // An answer from this browser (the card or a section) has settled or was refused.
  const answering = useEscalationStore((s) => (escalationId ? (s.answeringIds?.has(escalationId) ?? false) : false))
  const wasAnswering = useRef(false)
  useEffect(() => {
    if (wasAnswering.current && !answering) void reread()
    wasAnswering.current = answering
  }, [answering, reread])

  // The store learned the escalation is no longer pending (a section's poll or answer).
  const storedStatus = useEscalationStore((s) =>
    escalationId ? s.escalations.find((e) => e.id === escalationId)?.status : undefined,
  )
  useEffect(() => {
    if (open && storedStatus && storedStatus !== 'pending') void reread()
  }, [open, storedStatus, reread])

  return questionCardView(read)
}
